import { ImgID } from "../../../image_loader/img_id.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { formatChainVideoRemaining, getChainVideoRemainingMs } from "./chain_video_timer.js";
import { ChainVideoItem } from "./chain_video_item.js";
import { ChainExitReason, ChainScreenBase, ChainStepView, ChainTitleStyle, ChainView } from "../chain_offer_screen/chain_screen_base.js";
import { ChainItemBase, ChainItemConfig } from "../chain_offer_screen/chain_item_base.js";
import { OfferService } from "../../../offers/offer_service.js";
import { IapPlugin } from "../../../plugins/iap_plugin.js";
import { OfferStore } from "../../../api/dto/offers.dto.js";
import type { ChainOfferStepDto, OfferDto, OfferProductDto } from "../../../api/dto/offers.dto.js";
import { CHAIN_VIDEO_PLACEMENT } from "./chain_video_purchase_service.js";
import { Game } from "../../../game.js";

export class ChainVideoScreen extends ChainScreenBase
{
  // ---- look ----

  protected getBackgroundStops(): { offset: number; color: string }[]
  {
    return [
      { offset: 0, color: "#340246" },
      { offset: 1, color: "#6D0492" },
    ];
  }

  /** Widest ring first: each pass paints over the previous one, so the list runs
   *  outermost -> innermost and ends with the outline hugging the letters. */
  protected getTitleStyle(): ChainTitleStyle
  {
    return {
      font_size_design: 110,
      localization_key: "WAVE.MOVIE", // TEMP: placeholder until the real title key exists
      text_color: "#FCEA3D",
      layers: [
        { outline_width_design: 56, outline_offset_y_design: 11, outline_color: "#6C1F03" },
        { outline_width_design: 46, outline_offset_y_design: 2, outline_color: "#F9AF26" },
        { outline_width_design: 28, outline_offset_y_design: 6, outline_color: "#630374" },
        { outline_width_design: 22, outline_offset_y_design: 0, outline_color: "#B100FB" },
        { outline_width_design: 6, outline_offset_y_design: 3, outline_color: "#A85F01" },
      ],
    };
  }

  protected getTimerImgId(): ImgID
  {
    return ImgID.UI_ChainVideoTimerBg;
  }

  protected getRemainingMs(): number | null
  {
    return getChainVideoRemainingMs();
  }

  protected formatRemaining( ms: number | null ): string
  {
    return formatChainVideoRemaining( ms );
  }

  protected override createDecor(): void
  {
    this.createTopCat();
  }

  // ---- chain source ----

  protected getChangeEventName(): string
  {
    return 'offersEntitlementsChanged';
  }

  private getOffer(): OfferDto | null
  {
    return OfferService.getChainOffers( 'chain_video' )[0] ?? null;
  }

  private getPlatformProduct( step: ChainOfferStepDto ): OfferProductDto | null
  {
    const products = step.products;
    if ( !products ) return null;
    const store = IapPlugin.getStore();
    if ( store === OfferStore.GooglePlay ) return products.android ?? null;
    if ( store === OfferStore.AppStore ) return products.ios ?? null;
    return products.android ?? products.ios ?? null;
  }

  protected getChainView(): ChainView | null
  {
    const offer = this.getOffer();
    const chain = offer?.chain;
    if ( !offer || !chain ) return null;

    return {
      offer_id: offer.id,
      repeat: chain.repeat === true,
      active_step_id: chain.next_step_id ?? chain.next_paid_step_id ?? null,
      steps: chain.steps.map( step =>
      {
        const paid = !step.free && step.rewarded !== true;
        return {
          id: step.id,
          free: step.free,
          rewarded: step.rewarded,
          rewards: step.rewards,
          ...( paid && this.getPlatformProduct( step ) ? { price: '' } : {} ),
        };
      } ),
    };
  }

  protected override onItemCreated( item: ChainItemBase, step: ChainStepView ): void
  {
    if ( step.free || step.rewarded ) return;
    const offer = this.getOffer();
    const dto = offer?.chain?.steps.find( candidate => candidate.id === step.id );
    const product = dto ? this.getPlatformProduct( dto ) : null;
    if ( !offer || !product ) return;
    void this.loadPrice( item, offer.id, step.id, product );
  }

  private async loadPrice( item: ChainItemBase, offer_id: string, step_id: string, product: OfferProductDto ): Promise<void>
  {
    const context = { placement: CHAIN_VIDEO_PLACEMENT, chain_step_id: step_id, productid: product.productId };
    try
    {
      const details = await IapPlugin.getProductDetails( product.productId );
      item.setPrice( details?.formatted_price ?? 'Buy' );
      await OfferService.trackPriceLoaded( offer_id, context );
    }
    catch
    {
      item.setPrice( 'Buy' );
      await OfferService.trackPriceUnavailable( offer_id, context );
    }
  }

  protected createItem( x: number, y: number, width: number, height: number, config: ChainItemConfig ): ChainItemBase
  {
    return new ChainVideoItem( this.ctx, x, y, width, height, config );
  }

  protected override onShown( offer_id: string ): void
  {
    void OfferService.trackShown( offer_id, { placement: CHAIN_VIDEO_PLACEMENT } );
  }

  protected override onClosed( offer_id: string, reason: ChainExitReason ): void
  {
    void OfferService.trackClosed( offer_id, { placement: CHAIN_VIDEO_PLACEMENT, reason } );
  }

  private createTopCat(): void
  {
    const cat_w_min_design = 664;
    const cat_w_max_design = 1252;
    const cat_h_min_design = 490;
    const cat_h_max_design = 922;
    const cat_y_min_design = -9;
    const cat_y_max_design = -17;

    const cat_w = LayoutScaler.widthFromHeightLayouts( cat_w_min_design, cat_w_max_design );
    const cat_h = LayoutScaler.heightFromLayouts( cat_h_min_design, cat_h_max_design );
    const cat_x = ( this.rect.getW() - cat_w ) / 2;
    // The screen starts below the top safe area; like the chain offer's top
    // cloud, the art reaches up under the status bar instead of leaving an
    // empty strip there.
    const cat_y = LayoutScaler.yFromLayouts( cat_y_min_design, cat_y_max_design ) - Game.safeAreaTop();

    const top_cat = new UIImage( this.ctx, cat_x, cat_y, cat_w, cat_h, ImgID.UI_ChainVideoTopCat );
    this.addChild( top_cat );
  }

  public name(): string
  {
    return "ChainVideoScreen";
  }
}
